'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  Edit2,
  Trash2,
  ExternalLink,
  Search as SearchIcon,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  CheckSquare,
  Square,
  Minus,
  Check,
  X,
  Pencil,
  Sparkles,
  Columns,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import ConfirmModal from '@/components/ui/ConfirmModal';
import type { ClaimItem } from '@/lib/claimStore';
import { toast } from 'sonner';

interface InventoryTableProps {
  items: ClaimItem[];
  rooms: string[];
  members: string[];
  onDelete: (id: string) => void;
  onResearchPrice: (id: string) => void;
  onUpdate: (id: string, updates: Partial<ClaimItem>) => Promise<void>;
  onBulkUpdate: (ids: string[], updates: Partial<ClaimItem>) => Promise<void>;
  onBulkResearchPrices: (ids: string[]) => Promise<void>;
  totalCount: number;
}

type SortKey = 'name' | 'location' | 'category' | 'addedBy' | 'quantity' | 'unitPrice' | 'totalValue' | 'dateAdded' | 'priceStatus';
type SortDir = 'asc' | 'desc';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];

// ─── Column definitions ───────────────────────────────────────────────────────
type ColId = 'name' | 'category' | 'location' | 'addedBy' | 'quantity' | 'unitPrice' | 'total' | 'status' | 'source';

interface ColDef {
  id: ColId;
  label: string;
  defaultWidth: number;
  minWidth: number;
  sortKey?: SortKey;
  required?: boolean; // cannot be hidden
}

const COLUMNS: ColDef[] = [
  { id: 'name',      label: 'Item Name',  defaultWidth: 220, minWidth: 120, sortKey: 'name',        required: true },
  { id: 'category',  label: 'Category',   defaultWidth: 130, minWidth: 80,  sortKey: 'category' },
  { id: 'location',  label: 'Room',       defaultWidth: 130, minWidth: 80,  sortKey: 'location' },
  { id: 'addedBy',   label: 'Added By',   defaultWidth: 110, minWidth: 70,  sortKey: 'addedBy' },
  { id: 'quantity',  label: 'Qty',        defaultWidth: 70,  minWidth: 50,  sortKey: 'quantity' },
  { id: 'unitPrice', label: 'Unit Price', defaultWidth: 110, minWidth: 80,  sortKey: 'unitPrice' },
  { id: 'total',     label: 'Total',      defaultWidth: 110, minWidth: 80,  sortKey: 'totalValue' },
  { id: 'status',    label: 'Status',     defaultWidth: 110, minWidth: 80,  sortKey: 'priceStatus' },
  { id: 'source',    label: 'Source',     defaultWidth: 130, minWidth: 80 },
];

const DEFAULT_WIDTHS: Record<ColId, number> = Object.fromEntries(
  COLUMNS.map((c) => [c.id, c.defaultWidth])
) as Record<ColId, number>;

const DEFAULT_VISIBILITY: Record<ColId, boolean> = Object.fromEntries(
  COLUMNS.map((c) => [c.id, true])
) as Record<ColId, boolean>;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatCurrency(value: number) {
  if (value === 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatDate(dateStr: string) {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
}

const INPUT_METHOD_LABEL: Record<string, string> = {
  typed: '⌨',
  voice: '🎤',
  pasted: '📋',
};

const CONDITIONS = ['Destroyed', 'Damaged', 'Missing', 'Good', 'Fair', 'Poor'];

interface InlineEditState {
  name: string;
  description: string;
  location: string;
  category: string;
  quantity: string;
  unitPrice: string;
  condition: string;
  originalUnitPrice: string;
}

interface BulkEditState {
  location: string;
  quantity: string;
  unitPrice: string;
  condition: string;
  priceStatus: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function InventoryTable({
  items,
  rooms,
  members,
  onDelete,
  onResearchPrice,
  onUpdate,
  onBulkUpdate,
  onBulkResearchPrices,
  totalCount,
}: InventoryTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('dateAdded');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('inventoryPage');
      return saved ? parseInt(saved, 10) : 1;
    }
    return 1;
  });
  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('inventoryPerPage');
      const parsed = saved ? parseInt(saved, 10) : 10;
      return ITEMS_PER_PAGE_OPTIONS.includes(parsed) ? parsed : 10;
    }
    return 10;
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // ── Column visibility ──────────────────────────────────────────────────────
  const [colVisibility, setColVisibility] = useState<Record<ColId, boolean>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = sessionStorage.getItem('inventoryColVisibility');
        if (saved) return { ...DEFAULT_VISIBILITY, ...JSON.parse(saved) };
      } catch { /* ignore */ }
    }
    return DEFAULT_VISIBILITY;
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef<HTMLDivElement>(null);

  // ── Column widths ──────────────────────────────────────────────────────────
  const [colWidths, setColWidths] = useState<Record<ColId, number>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = sessionStorage.getItem('inventoryColWidths');
        if (saved) return { ...DEFAULT_WIDTHS, ...JSON.parse(saved) };
      } catch { /* ignore */ }
    }
    return DEFAULT_WIDTHS;
  });

  // Resize drag state (ref-based to avoid re-renders during drag)
  const resizingCol = useRef<ColId | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // Persist pagination
  useEffect(() => { sessionStorage.setItem('inventoryPage', String(page)); }, [page]);
  useEffect(() => { sessionStorage.setItem('inventoryPerPage', String(perPage)); }, [perPage]);

  // Persist column visibility
  useEffect(() => {
    sessionStorage.setItem('inventoryColVisibility', JSON.stringify(colVisibility));
  }, [colVisibility]);

  // Persist column widths
  useEffect(() => {
    sessionStorage.setItem('inventoryColWidths', JSON.stringify(colWidths));
  }, [colWidths]);

  // Close col picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target as Node)) {
        setColPickerOpen(false);
      }
    };
    if (colPickerOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [colPickerOpen]);

  // ── Resize handlers ────────────────────────────────────────────────────────
  const onResizeMouseDown = useCallback((e: React.MouseEvent, colId: ColId) => {
    e.preventDefault();
    e.stopPropagation();
    resizingCol.current = colId;
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = colWidths[colId];

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingCol.current) return;
      const col = COLUMNS.find((c) => c.id === resizingCol.current)!;
      const newWidth = Math.max(col.minWidth, resizeStartWidth.current + ev.clientX - resizeStartX.current);
      setColWidths((prev) => ({ ...prev, [resizingCol.current!]: newWidth }));
    };

    const onMouseUp = () => {
      resizingCol.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  // ── Inline edit state ──────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<InlineEditState>({
    name: '', description: '', location: '', category: '', quantity: '', unitPrice: '', condition: '', originalUnitPrice: '',
  });
  const [savingId, setSavingId] = useState<string | null>(null);

  // ── Bulk edit state ────────────────────────────────────────────────────────
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEdit, setBulkEdit] = useState<BulkEditState>({
    location: '', quantity: '', unitPrice: '', condition: '', priceStatus: '',
  });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResearching, setBulkResearching] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && nameInputRef.current) nameInputRef.current.focus();
  }, [editingId]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const sorted = [...items].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';
    switch (sortKey) {
      case 'name':        aVal = a.name;                  bVal = b.name;                  break;
      case 'location':    aVal = a.location;              bVal = b.location;              break;
      case 'category':    aVal = a.category ?? '';        bVal = b.category ?? '';        break;
      case 'addedBy':     aVal = a.addedBy;               bVal = b.addedBy;               break;
      case 'quantity':    aVal = a.quantity;              bVal = b.quantity;              break;
      case 'unitPrice':   aVal = a.unitPrice;             bVal = b.unitPrice;             break;
      case 'totalValue':  aVal = a.unitPrice * a.quantity; bVal = b.unitPrice * b.quantity; break;
      case 'dateAdded':   aVal = a.dateAdded;             bVal = b.dateAdded;             break;
      case 'priceStatus': aVal = a.priceStatus;           bVal = b.priceStatus;           break;
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const pageItems = sorted.slice((page - 1) * perPage, page * perPage);

  const allPageSelected = pageItems.length > 0 && pageItems.every((i) => selectedIds.has(i.id));
  const somePageSelected = pageItems.some((i) => selectedIds.has(i.id));

  const toggleAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => { const next = new Set(prev); pageItems.forEach((i) => next.delete(i.id)); return next; });
    } else {
      setSelectedIds((prev) => { const next = new Set(prev); pageItems.forEach((i) => next.add(i.id)); return next; });
    }
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const startEdit = (item: ClaimItem) => {
    setEditingId(item.id);
    setEditState({
      name: item.name, description: item.description, location: item.location,
      category: item.category ?? '', quantity: String(item.quantity),
      unitPrice: item.unitPrice > 0 ? String(item.unitPrice) : '', condition: item.condition,
      originalUnitPrice: item.unitPrice > 0 ? String(item.unitPrice) : '',
    });
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: string) => {
    const qty = parseInt(editState.quantity, 10);
    const price = parseFloat(editState.unitPrice);
    const originalPrice = parseFloat(editState.originalUnitPrice);
    if (!editState.name.trim()) { toast.error('Item name is required'); return; }
    if (isNaN(qty) || qty < 1) { toast.error('Quantity must be at least 1'); return; }
    setSavingId(id);
    // Auto-set price_researched if price was manually changed
    const priceChanged = !isNaN(price) && price > 0 && price !== originalPrice;
    const item = items.find((i) => i.id === id);
    const currentStatus = item?.priceStatus ?? 'unpriced';
    const newStatus = priceChanged ? 'price_researched' as const : currentStatus;
    await onUpdate(id, {
      name: editState.name.trim(), description: editState.description.trim(),
      location: editState.location, category: editState.category,
      quantity: qty, unitPrice: isNaN(price) ? 0 : price, condition: editState.condition,
      priceStatus: newStatus,
    });
    setSavingId(null);
    setEditingId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEdit(id); }
    if (e.key === 'Escape') cancelEdit();
  };

  const openBulkEdit = () => {
    setBulkEdit({ location: '', quantity: '', unitPrice: '', condition: '', priceStatus: '' });
    setBulkEditOpen(true);
  };

  const saveBulkEdit = async () => {
    const updates: Partial<ClaimItem> = {};
    if (bulkEdit.location) updates.location = bulkEdit.location;
    if (bulkEdit.quantity) { const qty = parseInt(bulkEdit.quantity, 10); if (!isNaN(qty) && qty >= 1) updates.quantity = qty; }
    if (bulkEdit.unitPrice) { const price = parseFloat(bulkEdit.unitPrice); if (!isNaN(price) && price >= 0) { updates.unitPrice = price; if (!bulkEdit.priceStatus) updates.priceStatus = 'price_researched'; } }
    if (bulkEdit.condition) updates.condition = bulkEdit.condition;
    if (bulkEdit.priceStatus) updates.priceStatus = bulkEdit.priceStatus as ClaimItem['priceStatus'];
    if (Object.keys(updates).length === 0) { toast.info('No changes to apply'); return; }
    setBulkSaving(true);
    await onBulkUpdate(Array.from(selectedIds), updates);
    setBulkSaving(false);
    setBulkEditOpen(false);
    setSelectedIds(new Set());
  };

  const handleBulkResearch = async () => {
    if (selectedIds.size === 0) return;
    setBulkResearching(true);
    await onBulkResearchPrices(Array.from(selectedIds));
    setBulkResearching(false);
    setSelectedIds(new Set());
  };

  const SortIcon = ({ colKey }: { colKey: SortKey }) => {
    if (sortKey !== colKey) return <ChevronsUpDown size={13} className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-primary" />
      : <ChevronDown size={13} className="text-primary" />;
  };

  const thClass = 'px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-foreground select-none transition-colors relative group/th';
  const tdClass = 'px-3 py-3 text-sm text-foreground align-top';
  const inputClass = 'w-full px-2 py-1 text-sm border border-primary/40 rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary';

  // Visible columns
  const visibleCols = COLUMNS.filter((c) => colVisibility[c.id]);
  const visibleCount = visibleCols.length;
  // total colspan = checkbox + visibleCols + actions
  const totalColspan = 1 + visibleCount + 1;

  // ── Resize handle ──────────────────────────────────────────────────────────
  const ResizeHandle = ({ colId }: { colId: ColId }) => (
    <span
      onMouseDown={(e) => onResizeMouseDown(e, colId)}
      className="absolute right-0 top-0 h-full w-3 flex items-center justify-center cursor-col-resize z-10 opacity-0 group-hover/th:opacity-100 hover:opacity-100 transition-opacity"
      title="Drag to resize column"
    >
      <span className="w-0.5 h-4 bg-border rounded-full" />
    </span>
  );

  return (
    <div className="bg-card border border-border rounded-xl">
      {/* ── Toolbar: column picker ─────────────────────────────────────────── */}
      <div className="flex items-center justify-end px-4 py-2 border-b border-border bg-secondary/20">
        <div className="relative" ref={colPickerRef}>
          <button
            onClick={() => setColPickerOpen((o) => !o)}
            className={[
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150',
              colPickerOpen
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-secondary',
            ].join(' ')}
            title="Choose visible columns"
          >
            <Columns size={13} />
            Columns
            {COLUMNS.filter((c) => !c.required && !colVisibility[c.id]).length > 0 && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary/20 text-primary text-[10px] font-bold">
                {COLUMNS.filter((c) => !c.required && !colVisibility[c.id]).length}
              </span>
            )}
          </button>

          {colPickerOpen && (
            <div className="absolute right-0 top-full mt-1.5 z-50 bg-card border border-border rounded-xl shadow-lg p-3 min-w-[180px]">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1">Show / Hide Columns</p>
              <div className="space-y-0.5">
                {COLUMNS.map((col) => {
                  const isVisible = colVisibility[col.id];
                  const isRequired = col.required;
                  return (
                    <label
                      key={col.id}
                      className={[
                        'flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm cursor-pointer transition-colors',
                        isRequired ? 'opacity-50 cursor-not-allowed' : 'hover:bg-secondary',
                      ].join(' ')}
                    >
                      <input
                        type="checkbox"
                        checked={isVisible}
                        disabled={isRequired}
                        onChange={() => {
                          if (isRequired) return;
                          setColVisibility((prev) => ({ ...prev, [col.id]: !prev[col.id] }));
                        }}
                        className="w-3.5 h-3.5 accent-primary rounded"
                      />
                      <span className={isVisible ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                        {col.label}
                      </span>
                      {isRequired && <span className="ml-auto text-[10px] text-muted-foreground">required</span>}
                    </label>
                  );
                })}
              </div>
              <div className="border-t border-border mt-2 pt-2 flex gap-2">
                <button
                  onClick={() => setColVisibility(DEFAULT_VISIBILITY)}
                  className="flex-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  Reset all
                </button>
                <button
                  onClick={() => {
                    const allOn = Object.fromEntries(COLUMNS.map((c) => [c.id, true])) as Record<ColId, boolean>;
                    setColVisibility(allOn);
                  }}
                  className="flex-1 text-xs text-primary hover:text-primary/80 transition-colors py-1 font-medium"
                >
                  Show all
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Bulk action bar ────────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border-b border-primary/20 slide-up flex-wrap">
          <span className="text-sm font-medium text-primary">{selectedIds.size} selected</span>
          <button
            onClick={handleBulkResearch}
            disabled={bulkResearching}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-lg hover:bg-primary/20 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {bulkResearching ? (
              <><div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Researching…</>
            ) : (
              <><Sparkles size={13} /> Research Prices <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">{selectedIds.size}</span></>
            )}
          </button>
          <button
            onClick={openBulkEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-lg hover:bg-primary/20 transition-all duration-150 active:scale-95"
          >
            <Pencil size={13} /> Edit Selected
          </button>
          <button
            onClick={() => setBulkDeleteOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-negative bg-negative-bg border border-negative/20 rounded-lg hover:bg-red-100 transition-all duration-150 active:scale-95"
          >
            <Trash2 size={13} /> Delete selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* ── Bulk edit panel ────────────────────────────────────────────────── */}
      {bulkEditOpen && (
        <div className="px-4 py-4 bg-primary/5 border-b border-primary/20">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-foreground">
              Bulk edit <span className="text-primary">{selectedIds.size} items</span>
              <span className="ml-2 text-xs font-normal text-muted-foreground">— leave a field blank to keep existing values</span>
            </p>
            <button onClick={() => setBulkEditOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Room</label>
              <select value={bulkEdit.location} onChange={(e) => setBulkEdit((s) => ({ ...s, location: e.target.value }))} className={inputClass}>
                <option value="">— keep existing —</option>
                {rooms.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Condition</label>
              <select value={bulkEdit.condition} onChange={(e) => setBulkEdit((s) => ({ ...s, condition: e.target.value }))} className={inputClass}>
                <option value="">— keep existing —</option>
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Quantity</label>
              <input type="number" min="1" placeholder="keep existing" value={bulkEdit.quantity} onChange={(e) => setBulkEdit((s) => ({ ...s, quantity: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Unit Price ($)</label>
              <input type="number" min="0" step="0.01" placeholder="keep existing" value={bulkEdit.unitPrice} onChange={(e) => setBulkEdit((s) => ({ ...s, unitPrice: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
              <select value={bulkEdit.priceStatus} onChange={(e) => setBulkEdit((s) => ({ ...s, priceStatus: e.target.value }))} className={inputClass}>
                <option value="">— keep existing —</option>
                <option value="unpriced">Unpriced</option>
                <option value="priced">Priced</option>
                <option value="price_researched">Price Researched</option>
                <option value="verified">Verified</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={saveBulkEdit}
              disabled={bulkSaving}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 transition-all duration-150 active:scale-95 disabled:opacity-60"
            >
              {bulkSaving ? (
                <><div className="w-3 h-3 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> Saving…</>
              ) : (
                <><Check size={13} /> Apply to {selectedIds.size} items</>
              )}
            </button>
            <button onClick={() => setBulkEditOpen(false)} className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg bg-card hover:bg-secondary transition-all duration-150">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ tableLayout: 'fixed', minWidth: `${visibleCols.reduce((s, c) => s + colWidths[c.id], 0) + 40 + 100}px` }}>
          <colgroup>
            {/* checkbox col */}
            <col style={{ width: 40 }} />
            {visibleCols.map((col) => (
              <col key={col.id} style={{ width: colWidths[col.id] }} />
            ))}
            {/* actions col */}
            <col style={{ width: 100 }} />
          </colgroup>

          <thead className="bg-secondary/50 border-b border-border">
            <tr>
              {/* Checkbox */}
              <th className="px-3 py-2.5 w-10">
                <button onClick={toggleAll} className="text-muted-foreground hover:text-foreground transition-colors">
                  {allPageSelected ? <CheckSquare size={16} className="text-primary" /> : somePageSelected ? <Minus size={16} className="text-primary" /> : <Square size={16} />}
                </button>
              </th>

              {/* Dynamic columns */}
              {visibleCols.map((col) => (
                <th
                  key={col.id}
                  className={col.sortKey ? thClass : `${thClass} cursor-default`}
                  onClick={col.sortKey ? () => handleSort(col.sortKey!) : undefined}
                  style={{ width: colWidths[col.id], overflow: 'hidden' }}
                >
                  <span className="flex items-center gap-1 pr-2 truncate">
                    {col.label}
                    {col.sortKey && <SortIcon colKey={col.sortKey} />}
                  </span>
                  <ResizeHandle colId={col.id} />
                </th>
              ))}

              {/* Actions */}
              <th className={`${thClass} cursor-default`}>Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={totalColspan} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                      <SearchIcon size={20} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-foreground">No items match your filters</p>
                    <p className="text-xs text-muted-foreground">Try adjusting the room, member, or status filters above</p>
                  </div>
                </td>
              </tr>
            ) : (
              pageItems.map((item, idx) => {
                const isEditing = editingId === item.id;
                const isSaving = savingId === item.id;
                return (
                  <tr
                    key={item.id}
                    className={[
                      'group transition-colors duration-100',
                      isEditing ? 'bg-primary/5 ring-1 ring-inset ring-primary/20' : selectedIds.has(item.id) ? 'bg-primary/5' : idx % 2 === 0 ? 'bg-card' : 'bg-secondary/20',
                      !isEditing && 'hover:bg-primary/5',
                    ].join(' ')}
                  >
                    {/* Checkbox */}
                    <td className="px-3 py-3 w-10">
                      <button onClick={() => toggleRow(item.id)} className="text-muted-foreground hover:text-primary transition-colors">
                        {selectedIds.has(item.id) ? <CheckSquare size={15} className="text-primary" /> : <Square size={15} />}
                      </button>
                    </td>

                    {/* Dynamic cells */}
                    {visibleCols.map((col) => {
                      switch (col.id) {
                        case 'name':
                          return (
                            <td key="name" className={tdClass} style={{ overflow: 'hidden' }}>
                              {isEditing ? (
                                <div className="space-y-1.5">
                                  <input ref={nameInputRef} type="text" value={editState.name} onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))} onKeyDown={(e) => handleKeyDown(e, item.id)} placeholder="Item name" className={inputClass} />
                                  <input type="text" value={editState.description} onChange={(e) => setEditState((s) => ({ ...s, description: e.target.value }))} onKeyDown={(e) => handleKeyDown(e, item.id)} placeholder="Description (optional)" className={inputClass} />
                                </div>
                              ) : (
                                <div className="overflow-hidden">
                                  <Link href={`/item-detail/${item.id}`} className="font-medium text-foreground hover:text-primary hover:underline truncate block transition-colors" title={item.name}>{item.name}</Link>
                                  <p className="text-xs text-muted-foreground truncate mt-0.5" title={item.description}>{item.description}</p>
                                  <span className="text-xs text-muted-foreground/60 mt-0.5 block" title={`Added via ${item.inputMethod}`}>{INPUT_METHOD_LABEL[item.inputMethod]}</span>
                                </div>
                              )}
                            </td>
                          );
                        case 'category':
                          return (
                            <td key="category" className={tdClass} style={{ overflow: 'hidden' }}>
                              {isEditing ? (
                                <input type="text" value={editState.category} onChange={(e) => setEditState((s) => ({ ...s, category: e.target.value }))} onKeyDown={(e) => handleKeyDown(e, item.id)} placeholder="Category" className={inputClass} />
                              ) : item.category ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-xs font-medium text-amber-800 whitespace-nowrap dark:bg-amber-900/20 dark:border-amber-700/40 dark:text-amber-300 truncate max-w-full">{item.category}</span>
                              ) : (
                                <span className="text-muted-foreground text-xs italic">—</span>
                              )}
                            </td>
                          );
                        case 'location':
                          return (
                            <td key="location" className={tdClass} style={{ overflow: 'hidden' }}>
                              {isEditing ? (
                                <select value={editState.location} onChange={(e) => setEditState((s) => ({ ...s, location: e.target.value }))} className={inputClass}>
                                  {rooms.map((r) => <option key={r} value={r}>{r}</option>)}
                                  {!rooms.includes(editState.location) && editState.location && <option value={editState.location}>{editState.location}</option>}
                                </select>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-secondary text-xs font-medium text-secondary-foreground whitespace-nowrap truncate max-w-full">{item.location}</span>
                              )}
                            </td>
                          );
                        case 'addedBy':
                          return (
                            <td key="addedBy" className={tdClass} style={{ overflow: 'hidden' }}>
                              <span className="text-sm text-foreground whitespace-nowrap truncate block">{item.addedBy.split(' ')[0]}</span>
                            </td>
                          );
                        case 'quantity':
                          return (
                            <td key="quantity" className={`${tdClass} text-center font-mono tabular-nums`} style={{ overflow: 'hidden' }}>
                              {isEditing ? (
                                <input type="number" min="1" value={editState.quantity} onChange={(e) => setEditState((s) => ({ ...s, quantity: e.target.value }))} onKeyDown={(e) => handleKeyDown(e, item.id)} className={`${inputClass} text-center`} />
                              ) : item.quantity}
                            </td>
                          );
                        case 'unitPrice':
                          return (
                            <td key="unitPrice" className={`${tdClass} font-mono tabular-nums whitespace-nowrap`} style={{ overflow: 'hidden' }}>
                              {isEditing ? (
                                <input type="number" min="0" step="0.01" placeholder="0.00" value={editState.unitPrice} onChange={(e) => setEditState((s) => ({ ...s, unitPrice: e.target.value }))} onKeyDown={(e) => handleKeyDown(e, item.id)} className={inputClass} />
                              ) : item.unitPrice > 0 ? formatCurrency(item.unitPrice) : <span className="text-muted-foreground text-xs italic">—</span>}
                            </td>
                          );
                        case 'total':
                          return (
                            <td key="total" className={`${tdClass} font-mono tabular-nums whitespace-nowrap font-medium`} style={{ overflow: 'hidden' }}>
                              {item.unitPrice > 0 ? formatCurrency(item.unitPrice * item.quantity) : <span className="text-muted-foreground text-xs italic">—</span>}
                            </td>
                          );
                        case 'status':
                          return (
                            <td key="status" className={tdClass} style={{ overflow: 'hidden' }}>
                              <StatusBadge status={item.priceStatus} size="sm" />
                            </td>
                          );
                        case 'source':
                          return (
                            <td key="source" className={tdClass} style={{ overflow: 'hidden' }}>
                              {item.priceResearch ? (
                                <a href={item.priceResearch.sourceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-info hover:underline whitespace-nowrap" title={item.priceResearch.sourceName} onClick={(e) => e.stopPropagation()}>
                                  <ExternalLink size={11} />
                                  {item.priceResearch.sourceName.length > 14 ? item.priceResearch.sourceName.slice(0, 14) + '…' : item.priceResearch.sourceName}
                                </a>
                              ) : (
                                <button onClick={() => onResearchPrice(item.id)} className="flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap font-medium transition-colors">
                                  <SearchIcon size={11} /> Research Price
                                </button>
                              )}
                            </td>
                          );
                        default:
                          return null;
                      }
                    })}

                    {/* Actions */}
                    <td className={tdClass}>
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => saveEdit(item.id)} disabled={isSaving} className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary text-primary-foreground rounded hover:bg-amber-700 transition-all duration-150 active:scale-95 disabled:opacity-60" title="Save changes">
                            {isSaving ? <div className="w-3 h-3 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> : <Check size={12} />}
                            Save
                          </button>
                          <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150" title="Cancel">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => startEdit(item)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary transition-all duration-150 active:scale-95" title="Edit inline">
                            <Pencil size={14} />
                          </button>
                          <Link href={`/item-detail/${item.id}`} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150 active:scale-95" title="Open detail page">
                            <Edit2 size={14} />
                          </Link>
                          <button onClick={() => setDeleteTarget(item.id)} className="p-1.5 rounded hover:bg-negative-bg text-muted-foreground hover:text-negative transition-all duration-150 active:scale-95" title="Delete item — this cannot be undone">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-secondary/20">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Show</span>
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="border border-border rounded px-2 py-1 bg-card text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => <option key={`pp-${n}`} value={n}>{n}</option>)}
          </select>
          <span>of <strong className="text-foreground">{totalCount}</strong> items</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-xs rounded border border-border bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150">
            Previous
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = totalPages <= 5 ? i + 1 : Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
            return (
              <button
                key={`page-${pageNum}`}
                onClick={() => setPage(pageNum)}
                className={['w-8 h-7 text-xs rounded border transition-all duration-150', page === pageNum ? 'bg-primary text-primary-foreground border-primary font-semibold' : 'border-border bg-card hover:bg-secondary text-muted-foreground'].join(' ')}
              >
                {pageNum}
              </button>
            );
          })}
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-xs rounded border border-border bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150">
            Next
          </button>
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete this item?"
        description="This item and its price research data will be permanently removed from your claim inventory. This cannot be undone."
        confirmLabel="Delete Item"
        onConfirm={() => {
          if (deleteTarget) { onDelete(deleteTarget); toast.success('Item removed from inventory'); }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
      <ConfirmModal
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} items?`}
        description="All selected items and their price research data will be permanently removed. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} Items`}
        onConfirm={() => {
          selectedIds.forEach((id) => onDelete(id));
          toast.success(`${selectedIds.size} items removed from inventory`);
          setSelectedIds(new Set());
          setBulkDeleteOpen(false);
        }}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </div>
  );
}